import { useEffect, useState, type ReactNode } from "react";

import { useAccountBalance } from "../hooks/useAccountBalance";
import { useRebates } from "../hooks/useRebates";
import { formatStamp, shortId } from "../lib/format";
import { groupThousands, parse, roundToFixed } from "../lib/money";
import { formatDollars } from "../lib/tape";

/**
 * Баланс до цента: в отличие от объёма тира, его сверяют с «Cash» на профиле
 * биржи цифра в цифру, и округление до десятых читалось бы как расхождение.
 */
export function formatCash(value: string | null | undefined): string {
  const m = parse(value);
  return m === null ? "—" : `$${groupThousands(roundToFixed(m, 2))}`;
}

/**
 * Карточка аккаунта: деньги на бирже, оборот за окно тира, путь до следующей
 * ступени и когда всё это последний раз сверялось с источником.
 *
 * Запросы живут, только пока карточка открыта: в таблице до десятка строк, и
 * опрашивать баланс каждой ради закрытого окна значило бы жечь лимит узла
 * впустую.
 */
export function AccountDetails({
  slot,
  funder,
  onClose,
}: {
  slot: string;
  funder: string;
  onClose: () => void;
}) {
  const balance = useAccountBalance(funder);
  const rebates = useRebates(funder);
  const [now, setNow] = useState(() => Date.now());

  // Давность обязана стареть сама: карточку держат открытой, а «только что»,
  // не сменившееся за десять минут, — ложь о свежести.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Escape не перехватывается: глобальный обработчик горячих клавиш снимает по
  // нему боевой режим, и закрытие карточки не вправе это отменить.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const b = balance.data;
  const r = rebates.data;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label={`Детали аккаунта ${slot}`}
        className="w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-5 text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium">Детали · {slot}</h2>
          <button
            type="button"
            className="text-[var(--color-muted)] hover:text-[var(--color-fg)]"
            title={`${funder} — скопировать`}
            onClick={() => void navigator.clipboard?.writeText(funder)}
          >
            {shortId(funder, 12)}
          </button>
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <Row label="Баланс">
            {balance.isError ? (
              <Muted>не получен</Muted>
            ) : !b ? (
              <Muted>…</Muted>
            ) : (
              <>
                <span className="num">{b.as_of ? formatCash(b.usd) : "—"}</span>
                {!b.known && <Warn>{b.reason ?? "нет данных"}</Warn>}
              </>
            )}
          </Row>

          <Row label={`Оборот, ${r?.window_days ?? 30} дн.`}>
            {rebates.isError ? (
              <Muted>не получен</Muted>
            ) : !r ? (
              <Muted>…</Muted>
            ) : !r.known ? (
              <Warn>{r.reason ?? "нет данных"}</Warn>
            ) : (
              <>
                <span
                  className="num"
                  title="взвешенный объём — та величина, по которой биржа считает тир"
                >
                  {r.complete ? "" : "≥ "}
                  {formatDollars(r.weighted_usd ?? "0")}
                </span>
                {!r.complete && (
                  <Warn>неполный: в окне есть необойдённые сутки</Warn>
                )}
              </>
            )}
          </Row>

          {r?.known && (
            <>
              <Row label="Уровень">{r.tier.name || "—"}</Row>
              <Row label="До следующего">
                {r.next ? (
                  <span>
                    {r.next.name} — осталось{" "}
                    <span className="num">{formatDollars(r.next.gap_usd)}</span>
                  </span>
                ) : (
                  <Muted>максимальный уровень</Muted>
                )}
              </Row>
            </>
          )}

          <Row label="Сверено">
            <span className="flex flex-col">
              <span title={b?.as_of}>баланс: {formatStamp(b?.as_of, now)}</span>
              <span title={r?.walked_at}>
                оборот: {formatStamp(r?.walked_at, now)}
              </span>
            </span>
          </Row>
        </dl>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-[var(--color-muted)]">{label}</dt>
      <dd className="flex flex-col">{children}</dd>
    </>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-[var(--color-muted)]">{children}</span>;
}

function Warn({ children }: { children: ReactNode }) {
  return <span className="text-[10px] text-amber-300">{children}</span>;
}
